/**
 * Reusing another student's setup of a song (2026-11-01; Anthony: "when a song
 * is entered for a student, we should be able to check if there is already a
 * created track / timed lyrics / etc and prompt the teacher to see if they
 * would like to just use those").
 *
 * Pure: the service (songReuseService.js) reads, this decides. Two halves:
 *
 *  - SOURCES. Every other student's live repertoire row for the same
 *    canonical song (`canonicalSongId`, the registry join; an unlinked row is
 *    invisible here for the same reason getRepertoireForSong gives), with
 *    what it would bring: its tracks, its words, its videos. A source that
 *    brings nothing is dropped. Ranked so the fullest setup is offered first.
 *
 *  - THE COPY. What lands on the new student. Song-level data is copied onto
 *    the new repertoire row, filling ONLY what that row lacks (never
 *    overwriting something a teacher already did for this student). Tracks
 *    are copied as POINTERS: the new student's studentTracks doc lists the
 *    same Storage files, so no audio is rendered or uploaded again.
 *
 * What is NEVER copied, and why:
 *  - a file in another student's own KEY (`key` / `keyOf`): that key was
 *    chosen for that voice. The original-key files come across, and Save as
 *    key is one tap from them.
 *  - recital / audition roles: a choice about one student's performance.
 *  - trims: where one student starts and stops singing.
 *  - a pending `lyrics.check` note: a question asked about the other row.
 *  - Song Notes: they are about the other student.
 */
import { staffVisibleFiles, trackDocMatchesSong } from './studentTracksModel';

const lower = (s) => String(s || '').trim().toLowerCase();

const isOwnKeyFile = (f) => Boolean(f && (f.key || f.keyOf));

/** The files of one student's pointer docs for a song that another student
 *  could use: reachable, not superseded, not in that student's own key.
 *  Docs are expected with admin edits already applied (fetchTracksForStudent),
 *  so a file an admin removed is not here and a rename travels as `label`. */
export const reusableFiles = (trackDocs = [], song = {}) => {
	const seen = new Set();
	const out = [];
	for (const d of Array.isArray(trackDocs) ? trackDocs : []) {
		if (!d || d.removedAt || !trackDocMatchesSong(d, song)) continue;
		for (const f of staffVisibleFiles(d.files)) {
			if (isOwnKeyFile(f)) continue;
			const k = f.storagePath || f.driveId || f.driveUrl;
			if (!k || seen.has(k)) continue;
			seen.add(k);
			out.push(f);
		}
	}
	return out;
};

/** Words worth copying: checked timing (lines on the clock, checked against
 *  a recording), or at least text. */
export const lyricsStateOf = (row) => {
	const l = row && row.lyrics;
	if (!l || typeof l.text !== 'string' || !l.text.trim()) return 'none';
	const timed = Array.isArray(l.timed) && l.timed.length > 0;
	if (timed && l.timing && l.timing.checked) return 'timed';
	return 'text';
};

const VIDEO_KINDS = ['lyrics', 'karaoke', 'performance'];
const videosOf = (row) => {
	const v = (row && row.videos) || {};
	const out = {};
	for (const k of VIDEO_KINDS) if (typeof v[k] === 'string' && v[k].trim()) out[k] = v[k].trim();
	return out;
};

const ms = (v) => {
	if (!v) return 0;
	if (typeof v.toMillis === 'function') return v.toMillis();
	if (typeof v.seconds === 'number') return v.seconds * 1000;
	const t = Date.parse(v);
	return Number.isFinite(t) ? t : 0;
};

/**
 * The sources a teacher can choose from, fullest first. `rows` are every
 * repertoire row for the canonical song (any student); `tracksByStudent` maps
 * a studentId to that student's pointer docs; `names` maps a studentId to a
 * first name for the label. The target student's own rows are skipped.
 */
export const reuseSources = ({ rows = [], tracksByStudent = {}, names = {}, studentId = null } = {}) => {
	const out = [];
	for (const row of Array.isArray(rows) ? rows : []) {
		if (!row || row.removedAt || !row.studentId || row.studentId === studentId) continue;
		const song = { canonicalSongId: row.canonicalSongId || null, name: row.songName };
		const files = reusableFiles(tracksByStudent[row.studentId] || [], song);
		const lyrics = lyricsStateOf(row);
		const videos = videosOf(row);
		const hasSections = Array.isArray(row.sections) && row.sections.length > 0;
		if (!files.length && lyrics === 'none' && !Object.keys(videos).length) continue;
		const playable = files.filter((f) => f.variant && lower(f.variant) !== 'sheet' && lower(f.variant) !== 'score');
		out.push({
			row,
			repertoireId: row.id,
			studentId: row.studentId,
			firstName: names[row.studentId] || null,
			files,
			trackCount: playable.length,
			hasTrack: files.some((f) => lower(f.variant) === 'track'),
			lyrics,
			videos,
			hasSections,
			lastWorkedMs: ms(row.lastWorkedOn) || ms(row.updatedAt),
		});
	}
	// A vocals-out Track is the thing a teacher cannot make on the spot, then
	// checked timed words, then how much there is, then the most recent.
	const rank = (s) => [s.hasTrack ? 1 : 0, s.lyrics === 'timed' ? 2 : s.lyrics === 'text' ? 1 : 0, s.trackCount, s.videos.lyrics ? 1 : 0, s.lastWorkedMs];
	return out.sort((a, b) => {
		const ra = rank(a);
		const rb = rank(b);
		for (let i = 0; i < ra.length; i += 1) if (ra[i] !== rb[i]) return rb[i] - ra[i];
		return 0;
	});
};

/** One line saying what a source brings, in the teacher's words. */
export const describeSource = (s) => {
	const parts = [];
	if (s.trackCount) {
		const kinds = [...new Set(s.files.map((f) => String(f.variant || '').trim()).filter(Boolean))];
		parts.push(`${s.trackCount === 1 ? '1 track' : `${s.trackCount} tracks`}${kinds.length ? ` (${kinds.join(', ')})` : ''}`);
	}
	if (s.lyrics === 'timed') parts.push('timed words');
	else if (s.lyrics === 'text') parts.push('words');
	if (s.videos.lyrics) parts.push('lyric video');
	const otherVideos = ['karaoke', 'performance'].filter((k) => s.videos[k]);
	if (otherVideos.length) parts.push(otherVideos.join(' and ') + (otherVideos.length === 1 ? ' video' : ' videos'));
	if (s.hasSections) parts.push('sections');
	return parts.join(' · ');
};

/**
 * The repertoire update for the new row: dot paths (CLAUDE.md, "{ merge: true }
 * is not deep"), and only for what `target` lacks. Returns { patch, brought }
 * where brought names what was actually copied. `nowIso` stamps provenance.
 */
export const songPatchFor = (source, target = {}, nowIso = new Date().toISOString()) => {
	const row = (source && source.row) || {};
	const patch = {};
	const brought = [];
	if (lyricsStateOf(target) === 'none' && lyricsStateOf(row) !== 'none') {
		// The whole map in one write is right HERE: the target has no words, so
		// there is nothing of its own to protect, and the pieces belong together.
		const { check, ...lyrics } = row.lyrics;
		patch.lyrics = lyrics;
		brought.push(lyricsStateOf(row) === 'timed' ? 'timed words' : 'words');
	}
	const theirs = videosOf(row);
	const mine = videosOf(target);
	for (const k of VIDEO_KINDS) {
		if (theirs[k] && !mine[k]) {
			patch[`videos.${k}`] = theirs[k];
			brought.push(k === 'lyrics' ? 'lyric video' : `${k} video`);
		}
	}
	// Sections are labelled FROM the words (sectionsMeta records which), so
	// they travel only with the words they were read from.
	if (patch.lyrics && Array.isArray(row.sections) && row.sections.length && !(Array.isArray(target.sections) && target.sections.length)) {
		patch.sections = row.sections;
		if (row.sectionsMeta) patch.sectionsMeta = row.sectionsMeta;
		brought.push('sections');
	}
	// Section nudges are about the RECORDING (keyed by its timeline), so they
	// hold for any student singing to that same file.
	const marks = row.sectionMarks && typeof row.sectionMarks === 'object' ? row.sectionMarks : {};
	const myMarks = target.sectionMarks && typeof target.sectionMarks === 'object' ? target.sectionMarks : {};
	for (const [key, value] of Object.entries(marks)) {
		if (/^m[a-z0-9]+$/.test(key) && value && typeof value === 'object' && !myMarks[key]) patch[`sectionMarks.${key}`] = value;
	}
	if (brought.length || Object.keys(patch).length) {
		patch.reusedFrom = { repertoireId: source.repertoireId || null, studentId: source.studentId || null, at: nowIso };
	}
	return { patch, brought };
};

/** A file as it goes onto the new pointer: the stored fields, without the
 *  read-side extras applyTrackEdits adds; a renamed file keeps its name. */
const storedFile = (f) => {
	const { editKey, docId, removedAt, removedBy, label, ...rest } = f;
	return label ? { ...rest, label } : rest;
};

/**
 * The new student's pointer doc after the copy. `existing` is their current
 * doc for the song (or null). Files already there (same Storage path) are not
 * added twice. Returns null when there is nothing to add. Never writes the
 * admin-only keys (removedAt / fileEdits / displayName): the rules refuse a
 * teacher those, and a create carrying them, even as null, is refused too.
 */
export const pointerFor = ({ studentId, studentName = null, song, files, existing = null }) => {
	const have = new Set(((existing && existing.files) || []).map((f) => f && f.storagePath).filter(Boolean));
	const add = (files || []).filter((f) => f && f.storagePath && !have.has(f.storagePath)).map(storedFile);
	if (!add.length) return null;
	return {
		studentId,
		studentName: studentName || (existing && existing.studentName) || null,
		songId: (song && song.canonicalSongId) || (existing && existing.songId) || null,
		songName: String((song && song.name) || '').trim(),
		songNameLower: lower(song && song.name),
		show: (existing && existing.show) || null,
		source: 'portal',
		files: [...((existing && existing.files) || []), ...add],
		added: add.length,
	};
};

/** The same doc id a track job files under (functions/trackJobs.js
 *  pointerDocId), so the next track made for this student joins these. */
export const pointerDocId = (studentId, songName) =>
	`${studentId}__portal__${
		String(songName || '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '')
			.slice(0, 80) || 'untitled'
	}`;
